import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { FragmentNames, NeonatalTpnInterruptAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import { AdminModalsService } from '../../../services/admin-modals-service';
import MedicationService from '../../../services/medication-service';
import { PrescriptionType } from '../../../services/timeline-service';
import { EventType } from '../../enteral-nutrition/enteral-nutrition-type';
import { NeonatalTpnType, ReasonType } from '../neonatal-tpn-types';
import NeonatalTpnUtils from '../neonatal-tpn-utils';

@Controller({ component: NeonatalTpnInterruptAliases.NEONATAL_TPN_INTERRUPT, fragment: FragmentNames.NEONATAL_TPN_INTERRUPT })
export default class NeonatalTpnInterruptController extends BaseController {

  @Inject(NeonatalTpnInterruptAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: TextAreaAPI;

  @Inject(NeonatalTpnInterruptAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnInterruptAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnInterruptAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(NeonatalTpnInterruptAliases.TITLE_FIELD)
  private titleFieldAPI!: FieldAPI

  @Inject(NeonatalTpnInterruptAliases.INTERRUPT_FORM)
  private interruptFormAPI!: FormAPI

  @Inject(NeonatalTpnInterruptAliases.REASON_AUTOCOMPLETE)
  private reasonAutocompleteAPI!: AutocompleteAPI;

  @OnMount(NeonatalTpnInterruptAliases.NEONATAL_TPN_INTERRUPT)
  handleLayoutInterruptMount(): void {
    const eventType = EventType.INTERRUPTION_OF_SOLUTIONS;
    const discardedVolume = 0;
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    this.context.set({ eventType, neonatalTpn });
    this.titleFieldAPI.setData({ materialDescription: neonatalTpn.materialDescription });
    this.interruptFormAPI.setData({
      ...neonatalTpn,
      discardedVolume,
      infusedVolume: neonatalTpn.totalVolume,
      stageVolume: neonatalTpn.totalVolume
    });
  }

  @OnMount(NeonatalTpnInterruptAliases.SCHEDULE_DATE_INPUT)
  handleScheduleDaeFieldMount(event: MountEvent): void {
    const { neonatalTpn } = this.context.get('neonatalTpn') as { neonatalTpn: NeonatalTpnType }
    const inputApi = event.target as InputAPI;
    inputApi.setValue(moment(neonatalTpn.scheduleDate).utc().format(ISO_FULL_DATE_FORMAT));
    inputApi.setReadOnly(true);
  }

  @OnBeforeCancel(NeonatalTpnInterruptAliases.INTERRUPT_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(NeonatalTpnInterruptAliases.REASON_FETCH)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: PrescriptionType.NEONATAL_PARENTERAL_NUTRITION }, { skip: true });
  }

  @OnMount(NeonatalTpnInterruptAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const inputApi = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnChange(NeonatalTpnInterruptAliases.DISCARDED_VOLUME_INPUT)
  handleInputChangeDiscardedVolume(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  @OnChange(NeonatalTpnInterruptAliases.INFUSED_VOLUME_INPUT)
  handleInputChangeInfusedVolume(event: ChangeEvent<number>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnBeforeSubmit(NeonatalTpnInterruptAliases.INTERRUPT_FORM)
  handleBeforeSubmit(): void {
    this.interruptFormAPI.validate();
    const body = this.getRequestBody();
    this.generatePresciptionChangesInterrupt(body)
  }

  private getRequestBody(): Record<string, Value> {
    const neonatalTpn = this.context.get('neonatalTpn')
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const itemType = this.historyService.getCurrentRouteParams().itemType as string;
    const reasonSelected = this.reasonAutocompleteAPI.getValue() as unknown as ReasonType;
    const changeTypeInterrupt = 2;

    return {
      changeDate: currentTime,
      changeType: changeTypeInterrupt,
      dateTime: null,
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue(),
      dosageType: null,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue(),
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string ?? reasonSelected.reasonDescription,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue(),
      prescriptionNumber: neonatalTpn.prescriptionNumber,
      reasonSequenceNumber: reasonSelected.sequenceNumber,
      solutionSequenceNumber: neonatalTpn.materialSequence,
      solutionType: NeonatalTpnUtils.getSolutionTypeByItemType(itemType),
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private generatePresciptionChangesInterrupt(body: Record<string, Value>): void {
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('tws.emar.interrupt-solution-success'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.NEONATAL_TPN_INTERRUPT);
    AdminModalsService.redirectToTimeline(FragmentNames.NEONATAL_TPN_VIEWER);
  }

  private handleVolumeInputChange(event: ChangeEvent<number>, input: InputAPI): void {
    const ZERO_VALUE = 0;
    const actualValue = event.value as number || ZERO_VALUE;
    const oldValue = event.oldValue;
    const neonatalTpn = this.context.get('neonatalTpn')
    if (actualValue && (actualValue !== oldValue)) {
      const partialVolume = this.partialVolumeInputAPI.getValue() as number;
      const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, neonatalTpn?.totalVolume);
      input.setValue(updatedVolume);
    }
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const ZERO_VALUE = 0;
    if (infusedVolume !== ZERO_VALUE && stageVolume > ZERO_VALUE) {
      const result = stageVolume - partialVolume - infusedVolume;
      return result < ZERO_VALUE ? ZERO_VALUE : result;
    }

    return ZERO_VALUE;
  }
}